/** Returns a promise that resolves after the given number of milliseconds. */
export function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Groups an array into a record keyed by a property name or selector function. */
export function keyBy<T>(array: T[], key: ((item: T) => string) | keyof T): Record<string, T> {
  const getKey = typeof key === 'function' ? key : (item: T) => String(item[key])
  return Object.fromEntries(array.map((item) => [getKey(item), item]))
}

/** Returns a shallow copy of the object, omitting entries where the predicate returns true. */
export function omitBy<T extends Record<string, unknown>>(
  obj: T,
  predicate: (value: T[keyof T], key: keyof T) => boolean
): Partial<T> {
  return (Object.keys(obj) as (keyof T)[]).reduce<Partial<T>>((acc, k) => {
    if (!predicate(obj[k], k)) acc[k] = obj[k]
    return acc
  }, {})
}

/** Merge multiple arrays, deduplicating by a key function. Later values win. */
export function mergeBy<T>(keyFn: (item: T) => string, ...arrays: T[][]): T[] {
  const map = new Map<string, T>()
  for (const arr of arrays) {
    for (const item of arr) map.set(keyFn(item), item)
  }
  return [...map.values()]
}

/**
 * Returns a shallow copy of the object with all `undefined` values removed.
 * biome-ignore lint/suspicious/noExplicitAny: best choice for generic lib function
 */
export function omitUndefined<T extends Record<string, any>>(
  obj: T
): { [K in keyof T as T[K] extends undefined ? never : K]: T[K] } {
  // biome-ignore lint/suspicious/noExplicitAny: this is cleaner than type gymnastics
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as any
}

/** Returns a new array with duplicates removed, keeping the first occurrence of each key. */
export function uniqueBy<T>(array: T[], key: ((item: T) => unknown) | keyof T): T[] {
  const seen = new Set<unknown>()
  const getKey = typeof key === 'function' ? key : (item: T) => item[key]
  return array.filter((item) => {
    const k = getKey(item)
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
}

/** Splits an array into chunks of `n` elements. The last chunk contains the remainder if the array length isn't divisible by n. */
export function chunk<T>(array: T[], n: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < array.length; i += n) {
    chunks.push(array.slice(i, i + n))
  }
  return chunks
}

/** Returns a shallow copy of the object with all nullish values removed. */
export function omitNullish<T extends Record<string, unknown>>(
  obj: T
): { [K in keyof T]?: Exclude<T[K], null | undefined> } {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null)
    // biome-ignore lint/suspicious/noExplicitAny: this is cleaner than type gymnastics
  ) as any
}
